import type React from 'react'
import { Skeleton } from './skeleton'

interface TableSkeletonProps {
    rows?: number
    columns?: number
    className?: string
    showSummary?: boolean
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
    rows = 5,
    columns = 6,
    className = '',
    showSummary = true,
}) => {
    return (
        <div className={`space-y-4 animate-pulse ${className}`}>
            {/* Header / Summary Bar */}
            {showSummary && (
                <div className="flex items-center justify-between p-4 bg-white/40 dark:bg-slate-900/40 backdrop-blur-md border border-white/20 dark:border-slate-800 rounded-2xl shadow-sm">
                    <div className="flex gap-3">
                        <Skeleton className="w-10 h-10 rounded-xl bg-indigo-100/50 dark:bg-indigo-900/20" />
                        <div className="space-y-2">
                            <Skeleton className="w-32 h-4 bg-slate-200/60 dark:bg-slate-700/60" />
                            <Skeleton className="w-20 h-3 bg-slate-200/60 dark:bg-slate-700/60" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Skeleton className="w-24 h-8 rounded-lg bg-white/50 dark:bg-slate-800/50" />
                        <Skeleton className="w-8 h-8 rounded-lg bg-white/50 dark:bg-slate-800/50" />
                    </div>
                </div>
            )}

            {/* Table Area */}
            <div className="border border-white/20 dark:border-slate-800 rounded-2xl overflow-hidden bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl shadow-2xl ring-1 ring-white/60 dark:ring-white/5">
                {/* Table Header */}
                <div className="flex border-b border-white/10 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 p-3 gap-2">
                    {Array.from({ length: columns }).map((_, i) => (
                        <Skeleton
                            key={`th-${i}`}
                            className={`h-8 rounded-lg ${i === 0 ? 'w-24' : 'flex-1'} bg-indigo-50/40 dark:bg-indigo-900/20`}
                        />
                    ))}
                </div>

                {/* Table Rows */}
                <div className="p-3 space-y-3">
                    {Array.from({ length: rows }).map((_, r) => (
                        <div key={`tr-${r}`} className="flex gap-2">
                            {Array.from({ length: columns }).map((_, c) => (
                                <Skeleton
                                    key={`td-${r}-${c}`}
                                    className={`h-10 rounded-lg ${c === 0 ? 'w-24' : 'flex-1'} ${r % 2 === 0 ? 'bg-white/40 dark:bg-slate-800/40' : 'bg-white/20 dark:bg-slate-800/20'}`}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
